import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  HardDrive,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Folder,
  Layers,
  Settings,
  HelpCircle,
  Clock,
  Mail,
  User,
  Sliders,
  Unlink,
  FileCheck,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { GoogleDriveConnection, GoogleDriveConnectionStatus } from '../types/index.js';

export const GoogleDriveIntegration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [connection, setConnection] = useState<GoogleDriveConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Editable settings state
  const [rootFolderName, setRootFolderName] = useState('EduRender');
  const [maxConcurrentUploads, setMaxConcurrentUploads] = useState(2);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [duplicatePolicy, setDuplicatePolicy] = useState<'skip_identical' | 'create_version'>('skip_identical');
  const [settingsSaved, setSettingsSaved] = useState(false);

  const loadIntegration = async () => {
    try {
      setLoading(true);
      const data = await api.getGoogleDriveIntegration();
      setConnection(data);
      if (data.settings) {
        setRootFolderName(data.settings.rootFolderName || 'EduRender');
        setMaxConcurrentUploads(data.settings.maxConcurrentUploads || 2);
        setMaxAttempts(data.settings.maxAttempts || 3);
        setDuplicatePolicy(data.settings.duplicatePolicy || 'skip_identical');
      }
    } catch (err) {
      console.error('Erro ao carregar integração do Google Drive:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegration();
    if (searchParams.get('status') === 'connected') {
      setTestResult({
        success: true,
        message: 'Conta Google Drive conectada com sucesso!',
      });
    }
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setTestResult({
        success: false,
        message: decodeURIComponent(errorParam),
      });
    }
  }, [searchParams]);

  const handleConnect = async () => {
    try {
      const redirectUrl = `${window.location.origin}/minha-conta?tab=integracoes`;
      const res = await api.connectGoogleDrive(redirectUrl);
      if (res.authUrl) {
        window.location.href = res.authUrl;
      }
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar conexão com o Google Drive.');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.testGoogleDriveConnection();
      setTestResult(res);
      await loadIntegration();
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Falha ao validar conexão com o Google Drive.',
      });
      await loadIntegration();
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const updated = await api.updateGoogleDriveSettings({
        rootFolderName: rootFolderName.trim() || 'EduRender',
        maxConcurrentUploads,
        maxAttempts,
        duplicatePolicy,
      });
      setConnection(updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || 'Falha ao salvar configurações.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Deseja realmente desconectar sua conta Google Drive? Transferências em andamento serão interrompidas.')) {
      return;
    }
    try {
      setLoading(true);
      await api.disconnectGoogleDrive();
      setTestResult(null);
      await loadIntegration();
    } catch (err: any) {
      alert(err.message || 'Falha ao desconectar Google Drive.');
    } finally {
      setLoading(false);
    }
  };

  // Helper badge formatting for the 8 specified states
  const getStatusBadge = (status: GoogleDriveConnectionStatus = 'not_configured') => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-status-success border border-green-200">
            <CheckCircle2 size={14} /> Conexão Válida
          </span>
        );
      case 'connected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-status-info border border-blue-200">
            <CheckCircle2 size={14} /> Conectado
          </span>
        );
      case 'validating':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-brand-primary border border-orange-200 animate-pulse">
            <RefreshCw size={14} className="animate-spin" /> Validando
          </span>
        );
      case 'connecting':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-brand-primary border border-orange-200">
            <RefreshCw size={14} className="animate-spin" /> Conectando
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock size={14} /> Conexão Expirada
          </span>
        );
      case 'authorization_error':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-status-error border border-red-200">
            <AlertTriangle size={14} /> Erro de Autorização
          </span>
        );
      case 'disconnected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-txt-muted border border-slate-200">
            <Unlink size={14} /> Desconectado
          </span>
        );
      case 'not_configured':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-txt-secondary border border-slate-200">
            <XCircle size={14} /> Não Configurado
          </span>
        );
    }
  };

  const isConnected =
    connection &&
    connection.connectionStatus !== 'not_configured' &&
    connection.connectionStatus !== 'disconnected';

  return (
    <div className="space-y-6">
      {/* Banner Informativo */}
      <div className="bg-gradient-to-r from-orange-50/80 to-amber-50/50 border border-orange-200/80 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white border border-orange-200 flex items-center justify-center shadow-xs shrink-0">
            {/* Google Drive SVG Icon */}
            <svg className="w-7 h-7" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
              <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
              <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
              <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
              <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
              <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
              <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-txt-primary">Integração com Google Drive V2.0</h2>
              {getStatusBadge(connection?.connectionStatus)}
            </div>
            <p className="text-xs text-txt-secondary mt-1 max-w-2xl leading-relaxed">
              Conecte sua conta Google oficial para transferir manualmente seus projetos e videoaulas finalizadas
              diretamente para pastas organizadas no seu Drive. Os arquivos MP4 preservam a ordem das aulas.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
          {isConnected ? (
            <>
              <EduButton
                variant="secondary"
                size="sm"
                onClick={handleTestConnection}
                disabled={testing}
                icon={<RefreshCw size={14} className={testing ? 'animate-spin' : ''} />}
              >
                {testing ? 'Testando...' : 'Testar Conexão'}
              </EduButton>
              <EduButton
                variant="destructive"
                size="sm"
                onClick={handleDisconnect}
                icon={<Unlink size={14} />}
              >
                Desconectar
              </EduButton>
            </>
          ) : (
            <EduButton
              variant="primary"
              size="md"
              onClick={handleConnect}
              icon={
                <svg className="w-4 h-4 mr-1" viewBox="0 0 87.3 78">
                  <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#ffffff"/>
                  <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#ffffff"/>
                  <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#ffffff"/>
                </svg>
              }
            >
              Conectar Google Drive
            </EduButton>
          )}
        </div>
      </div>

      {/* Alerta de Resultado do Teste de Conexão */}
      {testResult && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-3 transition-all ${
            testResult.success
              ? 'bg-green-50/90 border-green-200 text-green-900'
              : 'bg-red-50/90 border-red-200 text-red-900'
          }`}
        >
          {testResult.success ? (
            <CheckCircle2 size={18} className="text-status-success shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={18} className="text-status-error shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h4 className="text-xs font-bold">
              {testResult.success ? 'Conexão Validada com Sucesso' : 'Falha na Conexão'}
            </h4>
            <p className="text-xs mt-0.5 leading-relaxed">{testResult.message}</p>
          </div>
          <button
            onClick={() => setTestResult(null)}
            className="text-xs font-semibold hover:opacity-75"
          >
            ✕
          </button>
        </div>
      )}

      {/* Grid de Informações e Configurações */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card 1: Detalhes da Conexão */}
        <EduCard title="Conta Google Vinculada" className="lg:col-span-1">
          {loading ? (
            <div className="py-12 text-center text-xs text-txt-muted flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              <span>Carregando dados da conexão...</span>
            </div>
          ) : isConnected ? (
            <div className="space-y-4">
              <div className="p-3 bg-surface-secondary rounded-lg border border-bdr flex items-center gap-3">
                {connection?.googleAccountAvatar ? (
                  <img
                    src={connection.googleAccountAvatar}
                    alt="Conta Google"
                    className="w-10 h-10 rounded-full border border-bdr object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-brand-light text-brand-primary font-bold flex items-center justify-center text-sm">
                    {connection?.googleAccountName?.charAt(0) || 'G'}
                  </div>
                )}
                <div className="truncate">
                  <p className="text-xs font-bold text-txt-primary truncate">
                    {connection?.googleAccountName || 'Conta Google Autorizada'}
                  </p>
                  <p className="text-[11px] text-txt-secondary truncate flex items-center gap-1">
                    <Mail size={12} /> {connection?.googleAccountEmail || '—'}
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 text-xs">
                <div className="flex items-center justify-between py-1.5 border-b border-bdr/60">
                  <span className="text-txt-muted">Estado da Conexão</span>
                  {getStatusBadge(connection?.connectionStatus)}
                </div>

                <div className="flex items-center justify-between py-1.5 border-b border-bdr/60">
                  <span className="text-txt-muted">Pasta Raiz no Drive</span>
                  <span className="font-semibold text-txt-primary flex items-center gap-1 font-mono">
                    <Folder size={13} className="text-brand-primary" />
                    {connection?.rootFolderName || 'EduRender'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1.5 border-b border-bdr/60">
                  <span className="text-txt-muted">Data da Conexão</span>
                  <span className="text-txt-secondary font-mono text-[11px]">
                    {connection?.connectedAt
                      ? new Date(connection.connectedAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1.5 border-b border-bdr/60">
                  <span className="text-txt-muted">Último Teste Realizado</span>
                  <span className="text-txt-secondary font-mono text-[11px]">
                    {connection?.lastTestAt
                      ? new Date(connection.lastTestAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Nunca'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1.5">
                  <span className="text-txt-muted">Resultado do Teste</span>
                  <span
                    className={`font-semibold ${
                      connection?.lastTestStatus === 'success'
                        ? 'text-status-success'
                        : connection?.lastTestStatus === 'failed'
                        ? 'text-status-error'
                        : 'text-txt-muted'
                    }`}
                  >
                    {connection?.lastTestStatus === 'success'
                      ? 'Conexão Pronta'
                      : connection?.lastTestStatus === 'failed'
                      ? 'Atenção Necessária'
                      : 'Pendente'}
                  </span>
                </div>
              </div>

              <div className="pt-2">
                <EduButton
                  variant="secondary"
                  size="sm"
                  className="w-full justify-center"
                  onClick={handleTestConnection}
                  disabled={testing}
                  icon={<RefreshCw size={13} className={testing ? 'animate-spin' : ''} />}
                >
                  {testing ? 'Testando Permissões...' : 'Testar Conexão Agora'}
                </EduButton>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 text-txt-muted mx-auto flex items-center justify-center">
                <HardDrive size={22} />
              </div>
              <h4 className="text-sm font-bold text-txt-primary">Nenhuma Conta Conectada</h4>
              <p className="text-xs text-txt-secondary max-w-xs mx-auto leading-relaxed">
                Autorize sua conta Google através do login oficial para habilitar transferências e exportações diretas.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                onClick={handleConnect}
                className="mt-2"
              >
                Conectar Agora
              </EduButton>
            </div>
          )}
        </EduCard>

        {/* Card 2: Parâmetros e Regras de Organização */}
        <EduCard title="Parâmetros de Configuração e Envio" className="lg:col-span-2">
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pasta Raiz */}
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Nome da Pasta Principal no Google Drive
                </label>
                <div className="relative">
                  <Folder size={16} className="absolute left-3 top-2.5 text-brand-primary" />
                  <input
                    type="text"
                    required
                    value={rootFolderName}
                    onChange={(e) => setRootFolderName(e.target.value)}
                    placeholder="EduRender"
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
                  />
                </div>
                <p className="text-[11px] text-txt-muted mt-1">
                  Pasta raiz criada na raiz do seu Drive onde todos os projetos serão organizados.
                </p>
              </div>

              {/* Política de Duplicidade */}
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Política de Duplicidade
                </label>
                <select
                  value={duplicatePolicy}
                  onChange={(e) => setDuplicatePolicy(e.target.value as any)}
                  className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value="skip_identical">Não reenviar vídeos já transferidos (Padrão)</option>
                  <option value="create_version">Permitir nova versão do arquivo quando solicitado</option>
                </select>
                <p className="text-[11px] text-txt-muted mt-1">
                  Evita o consumo desnecessário de tráfego de rede e tempo de transferência.
                </p>
              </div>

              {/* Uploads Simultâneos */}
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Uploads Simultâneos por Lote
                </label>
                <select
                  value={maxConcurrentUploads}
                  onChange={(e) => setMaxConcurrentUploads(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value={1}>1 upload por vez (Menor uso de banda)</option>
                  <option value={2}>2 uploads simultâneos (Recomendado)</option>
                  <option value={3}>3 uploads simultâneos</option>
                </select>
                <p className="text-[11px] text-txt-muted mt-1">
                  Controla o paralelismo do worker para não concorrer com as renderizações.
                </p>
              </div>

              {/* Tentativas Máximas */}
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Tentativas em Caso de Falha de Rede
                </label>
                <select
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-surface border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value={2}>2 tentativas com backoff</option>
                  <option value={3}>3 tentativas (Padrão)</option>
                  <option value={5}>5 tentativas</option>
                </select>
                <p className="text-[11px] text-txt-muted mt-1">
                  Backoff exponencial automático para tratar oscilações na rede.
                </p>
              </div>
            </div>

            {/* Informações da Estrutura de Pastas e Nomes */}
            <div className="p-4 bg-surface-secondary rounded-xl border border-bdr space-y-2">
              <h4 className="text-xs font-bold text-txt-primary flex items-center gap-1.5">
                <FileCheck size={14} className="text-brand-primary" /> Regras de Organização Garantidas:
              </h4>
              <ul className="text-xs text-txt-secondary space-y-1 list-disc list-inside">
                <li>
                  <strong>Uma pasta exclusiva por projeto:</strong> vinculada ao identificador interno do projeto.
                </li>
                <li>
                  <strong>Nomes de arquivos padronizados:</strong> <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px] font-mono">Aula 01 - Título da Aula.mp4</code>.
                </li>
                <li>
                  <strong>Sem envio automático:</strong> o envio ao Drive é 100% manual e sob seu comando.
                </li>
                <li>
                  <strong>Sem consumo de franquia:</strong> transferir para o Google Drive não consome novas aulas da sua assinatura mensal.
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-between pt-2">
              {settingsSaved && (
                <span className="text-xs text-status-success font-semibold flex items-center gap-1">
                  <CheckCircle2 size={14} /> Configurações salvas com sucesso!
                </span>
              )}
              <EduButton
                type="submit"
                variant="primary"
                size="sm"
                className="ml-auto"
                disabled={savingSettings || !isConnected}
              >
                {savingSettings ? 'Salvando...' : 'Salvar Parâmetros'}
              </EduButton>
            </div>
          </form>
        </EduCard>
      </div>
    </div>
  );
};
